import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import * as React from 'react';
import { StyleSheet } from 'aphrodite';
import { Link, useInRouterContext } from 'react-router-dom-v5-compat';
import { addStyle } from '@khanacademy/wonder-blocks-core';
import { isClientSideUrl } from '@khanacademy/wonder-blocks-clickable';
import { PhosphorIcon } from '@khanacademy/wonder-blocks-icon';
import { createThemeContext, ThemeSwitcherContext, useScopedTheme } from '@khanacademy/wonder-blocks-theming';
import { focusStyles } from '@khanacademy/wonder-blocks-styles';
import { semanticColor, border } from '@khanacademy/wonder-blocks-tokens';

const iconSizeForButtonSize = size => {
  switch (size) {
    case "xsmall":
      return "small";
    case "small":
      return "medium";
    case "medium":
      return "medium";
    case "large":
      return "medium";
  }
};
const targetPixelsForSize = size => ({
  xsmall: 24,
  small: 32,
  medium: 40,
  large: 48
})[size];

const theme = {
  color: semanticColor.action,
  border: {
    offset: {
      primary: border.width.medium,
      secondary: `-${border.width.medium}`,
      tertiary: -border.width.medium
    },
    width: {
      primary: {
        default: border.width.none,
        hover: border.width.medium,
        press: border.width.medium
      },
      secondary: {
        default: border.width.thin,
        hover: border.width.medium,
        press: border.width.medium
      },
      tertiary: {
        default: border.width.none,
        hover: border.width.medium,
        press: border.width.medium
      }
    },
    radius: {
      default: border.radius.radius_040
    }
  }
};

const themes = {
  default: theme
};
const IconButtonThemeContext = createThemeContext(theme);
function ThemedIconButton(props) {
  var _themes$currentTheme;
  const currentTheme = React.useContext(ThemeSwitcherContext);
  const theme$1 = (_themes$currentTheme = themes[currentTheme]) != null ? _themes$currentTheme : theme;
  return React.createElement(IconButtonThemeContext.Provider, {
    value: theme$1
  }, props.children);
}

const _excluded$1 = ["actionType", "disabled", "href", "icon", "kind", "size", "skipClientNav", "style", "testId", "type"];
function IconChooser({
  icon,
  size
}) {
  const iconSize = iconSizeForButtonSize(size);
  switch (iconSize) {
    case "small":
      return React.createElement(PhosphorIcon, {
        size: "small",
        color: "currentColor",
        icon: icon
      });
    case "medium":
    default:
      return React.createElement(PhosphorIcon, {
        size: "medium",
        color: "currentColor",
        icon: icon
      });
  }
}
const StyledA = addStyle("a");
const StyledButton = addStyle("button");
const StyledLink = addStyle(Link);
const IconButtonCore = React.forwardRef(function IconButtonCore(props, ref) {
  const {
      actionType,
      disabled,
      href,
      icon,
      kind = "primary",
      size = "medium",
      skipClientNav,
      style,
      testId,
      type = "button"
    } = props,
    restProps = _objectWithoutPropertiesLoose(props, _excluded$1);
  const {
    theme,
    themeName
  } = useScopedTheme(IconButtonThemeContext);
  const inRouterContext = useInRouterContext();
  const buttonStyles = _generateStyles(actionType, !!disabled, kind, size, theme, themeName);
  const defaultStyle = [sharedStyles.shared, buttonStyles.default, disabled && buttonStyles.disabled];
  const child = React.createElement(IconChooser, {
    size: size,
    icon: icon
  });
  const commonProps = _extends({
    "data-testid": testId,
    style: [defaultStyle, style]
  }, restProps);
  if (href && !disabled) {
    return inRouterContext && !skipClientNav && isClientSideUrl(href) ? React.createElement(StyledLink, _extends({}, commonProps, {
      to: href,
      ref: ref
    }), child) : React.createElement(StyledA, _extends({}, commonProps, {
      href: href,
      ref: ref
    }), child);
  } else {
    return React.createElement(StyledButton, _extends({
      type: type
    }, commonProps, {
      onClick: disabled ? undefined : restProps.onClick,
      "aria-disabled": disabled,
      ref: ref
    }), child);
  }
});
const sharedStyles = StyleSheet.create({
  shared: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    padding: 0,
    cursor: "pointer",
    border: "none",
    outline: "none",
    textDecoration: "none",
    background: "none",
    margin: 0,
    touchAction: "manipulation"
  }
});
const styles = {};
const _generateStyles = (actionType = "progressive", disabled, kind, size, theme, themeName) => {
  const buttonType = `${actionType}-d_${disabled}-${kind}-${size}-${themeName}`;
  if (styles[buttonType]) {
    return styles[buttonType];
  }
  const pixelsForSize = targetPixelsForSize(size);
  const borderWidthKind = theme.border.width[kind];
  const outlineOffsetKind = theme.border.offset[kind];
  const themeVariant = theme.color[kind][actionType];
  const disabledState = theme.color[kind].disabled;
  const disabledStatesStyles = {
    borderColor: disabledState.border,
    borderWidth: borderWidthKind.default,
    background: disabledState.background,
    color: disabledState.foreground
  };
  const newStyles = {
    default: _extends({
      height: pixelsForSize,
      width: pixelsForSize,
      borderRadius: theme.border.radius.default,
      borderStyle: "solid",
      borderWidth: borderWidthKind.default,
      borderColor: themeVariant.default.border,
      background: themeVariant.default.background,
      color: themeVariant.default.foreground,
      ":hover": {
        background: themeVariant.hover.background,
        color: themeVariant.hover.foreground,
        outline: kind === "primary" ? `${borderWidthKind.hover} solid ${themeVariant.hover.border}` : undefined,
        outlineOffset: kind === "primary" ? outlineOffsetKind : undefined,
        border: kind !== "primary" ? `${borderWidthKind.hover} solid ${themeVariant.hover.border}` : undefined
      },
      ["@media not (hover: hover)"]: {
        ":hover": {
          backgroundColor: "transparent"
        }
      },
      ":active": {
        outline: kind === "primary" ? `${borderWidthKind.press} solid ${themeVariant.press.border}` : undefined,
        outlineOffset: kind === "primary" ? outlineOffsetKind : undefined,
        border: kind !== "primary" ? `${borderWidthKind.press} solid ${themeVariant.press.border}` : undefined,
        background: themeVariant.press.background,
        color: themeVariant.press.foreground
      }
    }, focusStyles.focus),
    disabled: _extends({
      cursor: "not-allowed"
    }, disabledStatesStyles, {
      ":hover": _extends({}, disabledStatesStyles, {
        outline: "none"
      }),
      ":active": _extends({}, disabledStatesStyles, {
        outline: "none"
      }),
      ":focus-visible": disabledStatesStyles
    })
  };
  styles[buttonType] = StyleSheet.create(newStyles);
  return styles[buttonType];
};

const _excluded = ["actionType", "disabled", "href", "kind", "size", "skipClientNav", "tabIndex", "target", "type"];
const IconButton = React.forwardRef(function IconButton(props, ref) {
  const {
      actionType = "progressive",
      disabled = false,
      href,
      kind = "primary",
      size = "medium",
      skipClientNav,
      tabIndex,
      target,
      type
    } = props,
    sharedProps = _objectWithoutPropertiesLoose(props, _excluded);
  function handleKeyDown(e) {
    const keyCode = e.key;
    if (!href && (keyCode === "Enter" || keyCode === "Space")) {
      e.preventDefault();
    }
  }
  function handleKeyUp(e) {
    const keyCode = e.key;
    if (!href && (keyCode === "Enter" || keyCode === "Space")) {
      if (sharedProps.onClick) {
        sharedProps.onClick(e);
      }
    }
  }
  return React.createElement(ThemedIconButton, null, React.createElement(IconButtonCore, _extends({}, sharedProps, {
    actionType: actionType,
    disabled: disabled,
    href: href,
    kind: kind,
    ref: ref,
    skipClientNav: skipClientNav,
    size: size,
    target: target,
    tabIndex: tabIndex,
    onKeyDown: handleKeyDown,
    onKeyUp: handleKeyUp,
    type: type
  })));
});

export { IconButton as default };
